<template>
  <div class="outer-container">
    <div class="mode-info f-r-vc">
      <span class="title" v-if="pageMode === ShipmentCreation">
        New Shipment
      </span>
      <div class="f-r f-r-vc" style="gap: 10px" v-if="pageMode === ShipmentUpdate">
        <span class="title"> Update Shipment </span>
        <el-link type="primary" style="padding-top: 2px" v-if="pageMode === ShipmentUpdate"
          @click="goToShipmentDetail(shipmentNo, { tab: 'PalletsTab' })" class="title">
          {{ shipmentNo }}
        </el-link>
      </div>
      <div class="table-header">
        <div class="table-row-status-indicator">
          <div class="status-spot stock-incart-indicator"></div>
          <span class="status-title">In Cart</span>
        </div>
      </div>
      <div class="mode-actions f-r">
        <el-button size="mini" type="primary" @click="openNewDialog" v-if="pageMode === ShipmentModes.ShipmentCreation">
          New
        </el-button>
        <el-button size="mini" type="success" @click="updateShipment" v-if="pageMode === ShipmentModes.ShipmentUpdate">
          Update
        </el-button>
        <el-button size="mini" plain type="danger" @click="clearCartList">
          Clear
        </el-button>
      </div>
    </div>
    <div class="page-container f-r">
      <div class="left" :class="{ animate: showCart }">
        <div class="stock-list shadow-level-three" title="Stock (ready for shipments)" v-loading="loadingStock">
          <div class="f-r header">
            <el-tabs v-model="selectedTab">
              <el-tab-pane v-for="tab in Tabs" :key="tab.value" :label="tab.label" :name="tab.value" />
            </el-tabs>
            <div>
              <el-button size="small" type="primary" @click="openCart" v-if="selectedTab === UNHOLD">
                Cart
              </el-button>
            </div>
          </div>
          <searchable-table :table-headers="stockTableHeaders" :table-data="stockPage" :search-params="searchForm"
            :search-param-change="searchParamChange" :action-width="80"
            :has-actions="[UNHOLD, AddressGroup].includes(selectedTab)" :has-row-expand="true"
            :rowClassName="rowGroupClass">
            <template v-slot:row-expand="props">
              <keep-alive>
                <div class="group-expands">
                  <div class="tables f-c">
                    <pallet-list :palletQuery="{
                      ids: props.row.palletIds
                    }" :showActions="false" :showGroupName="false" :showHeaderOps="false"
                      :selectable="selectedTab === HOLD" :releasable="true" />
                    <packing-list :queryParams="props.row.goodsIds.length > 0
                      ? { ids: props.row.goodsIds }
                      : null
                      " :showActions="false" :showHeaderOps="false" />
                  </div>
                </div>
              </keep-alive>
            </template>
            <template v-slot:actions="props">
              <div class="actions f-r">
                <el-button size="small" type="text" @click="addToCart(props.row)"
                  :disabled="props.row.palletIds.length === 0">
                  <svg-icon icon-class="plus" class="svg-business-color" style="font-size: 20px" />
                </el-button>
              </div>
            </template>
            <template #[inCartCount]="props">
              <span class="in-cart-count bold-oswald">
                {{ props.row.inCartCount }}
              </span>
            </template>
            <template #[totalPalletCount]="props">
              <span class="in-cart-count bold-oswald">
                {{ props.row.palletIds.length }}
              </span>
            </template>
          </searchable-table>
        </div>
      </div>
      <div class="right shadow-level-three" :class="{ animate: showCart }">
        <div class="cart-header f-r-c-sb">
          <span class="selected-counts">{{ totalSelected }}/{{ MAX_PALLETS_IN_SHIPMENT }}</span>
          <el-button size="mini" type="primary" plain @click="exportPOs">
            PO Export
          </el-button>
        </div>
        <div class="cart-list" v-loading="loadingCart">
          <div class="pallet-selected f-c">
            <div v-for="group in cartList" :key="group.id" class="cart-group">
              <div class="first f-r-hsb">
                <div class="f-c" style="gap: 0">
                  <address-tag class="group-address" :address="group.address" />
                  <span class="group-order-no">{{ group.riiOrderNo }}</span>
                </div>
                <div class="others f-r-c-c">
                  <span class="pallet-counts">
                    {{ group.palletIds.length }}
                  </span>
                  <svg-icon icon-class="trash" class="svg-danger-color" style="font-size: 26px; margin-left: 5px"
                    @click.native="removeFromCart(group)" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <common-dialog ref="dialog" title="Please select" :showResetBtn="false" :width="32">
      <template v-slot:content>
        <div class="new-shipment-or-job f-r">
          <div class="left f-c-c-c" @click="createShipment">
            <span class="but-text">New Shipment</span>
          </div>
          <div class="right f-c-c-c" @click="createSplitJob">
            <span class="but-text">Split Pallets</span>
          </div>
        </div>
      </template>
    </common-dialog>
  </div>
</template>
<script>
import debounce from 'lodash/debounce'
import set from 'lodash/set'

// mixins
import { pageJumps } from '@/core/mixin/pages'
// apis
import { searchStock } from '@/api/pallet'
import { modifyShipmentPallets, queryShipment } from '@/api/shipment'
import { queryAddress } from '@/api/address'
import { queryOrders } from '@/api/order'
import * as XLSX from 'xlsx'
// models
import {
  HOLD,
  UNHOLD,
  SHIPPED,
  MAX_PALLETS_IN_SHIPMENT
} from '@/core/models/constant'
import {
  NormalPallet,
  MediumPallet,
  HighPallet,
  PalletDelivered,
  PalletUpcoming,
  StockSearchForm,
  StockSearchFormWrapUp,
  StockPalletTableHeaders,
  StockUnHoldTableHeaders,
  StockHoldTableHeaders,
  StockShippedTableHeaders,
  StockAddressTableHeaders,
  StockDefaultSort
} from '@/core/models/pallet'
import { StockGroupGoodsTableHeaders } from '@/core/models/goods'
import {
  ShipmentCreation,
  ShipmentModes,
  ShipmentUpdate
} from '@/core/models/shipment'
import { TabsMixin } from '@/core/mixin/tabs'
// components
import SearchableTable from '@/components/common/SearchableTable.vue'
import PalletList from '@/components/pallet/PalletList.vue'
import PackingList from '@/components/goods/PackingList.vue'
import AddressTag from '@/components/address/AddressTag.vue'
import { InitSearchForm } from '@/core/configs/types'
import { LocalOrder, OrderCreated, OrderProcessing } from '@/core/models/order'
import CommonDialog from '@/components/common/CommonDialog.vue'
// msg
import PagesMessage from '@/core/message/pages'
import { SplitJobCreate } from '@/core/models/job'
import { queryGoods } from '@/api/goods'

const AddressGroup = 'AddressGroup'

export default {
  name: 'StockList',
  components: {
    SearchableTable,
    AddressTag,
    PalletList,
    PackingList,
    CommonDialog
  },
  mixins: [pageJumps, TabsMixin],
  data() {
    return {
      AddressGroup: AddressGroup,
      PalletDelivered,
      PalletUpcoming,
      NormalPallet,
      MediumPallet,
      HighPallet,
      ShipmentCreation,
      ShipmentUpdate,
      HOLD,
      UNHOLD,
      SHIPPED,
      StockGroupGoodsTableHeaders,
      StockPalletTableHeaders,
      MAX_PALLETS_IN_SHIPMENT,
      stockPage: null,
      loadingStock: false,
      loadingCart: false,
      loadingGoods: true,
      searchForm: InitSearchForm(StockSearchForm, StockDefaultSort),
      showCart: true,
      cartList: [],
      ShipmentModes,
      shipmentDetail: '',
      selectedTab: AddressGroup,
      inCartCount: 'inCartCount',
      totalPalletCount: 'totalPalletCount',
      debouncedFetch: debounce(this.doStockSearch, 300)
    }
  },
  computed: {
    pageMode() {
      return this.$route.query.mode
    },
    shipmentNo() {
      return this.$route.query.no ?? ''
    },
    totalSelected() {
      return this.cartList.reduce(
        (prev, group) => prev + group.palletIds.length,
        0
      )
    },
    Tabs() {
      return [
        { label: 'Ready to Ship', value: UNHOLD },
        { label: 'Address Group', value: AddressGroup },
        { label: 'Hold', value: HOLD },
        { label: 'Shipped', value: SHIPPED }
      ]
    },
    stockTableHeaders() {
      switch (this.selectedTab) {
        case UNHOLD:
          return StockUnHoldTableHeaders
        case HOLD:
          return StockHoldTableHeaders
        case SHIPPED:
          return StockShippedTableHeaders
        default:
          return StockAddressTableHeaders
      }
    }
  },
  watch: {
    cartList: {
      handler() {
        if (this.pageMode === ShipmentCreation) {
          localStorage.setItem('cartList', JSON.stringify(this.cartList))
        }
        this.syncGroupSelected()
        // assign
      },
      deep: true
    },
    pageMode: {
      handler() {
        switch (this.pageMode) {
          case ShipmentCreation:
            this.$set(
              this,
              'cartList',
              JSON.parse(localStorage.getItem('cartList')) ?? []
            )
            break
          case ShipmentUpdate:
            break
          default:
            this.goToStock({ mode: ShipmentCreation }, true)
        }
      },
      immediate: true
    },
    shipmentNo: {
      handler() {
        if (this.pageMode === ShipmentUpdate) {
          if (!this.shipmentNo) {
            this.goToStock({ mode: ShipmentCreation }, true)
          } else {
            this.getShipmentDetail()
          }
        }
      },
      immediate: true
    },
    selectedTab() {
      if ([HOLD, SHIPPED].includes(this.selectedTab)) {
        this.showCart = false
      } else {
        this.showCart = true
      }
      this.debouncedFetch()
    }
  },
  methods: {
    searchParamChange(key, path, value) {
      set(this.searchForm[key], path, value)
      this.debouncedFetch()
    },
    rowGroupClass(row) {
      let classes = 'row_min_height '
      if (row.inCartCount > 0) {
        classes += 'stock-row-incart '
      }
      return classes
    },
    openCart() {
      this.showCart = !this.showCart
    },
    clearCartList() {
      this.$set(this, 'cartList', [])
    },
    addToCart(group) {
      if (this.selectedTab === AddressGroup) {
        this.addAddressStockGroup(group)
      } else {
        this.addStockGroup(group)
      }
    },
    addStockGroup(group) {
      const index = this.cartList.findIndex(
        one =>
          one.orderId === group.orderId && one.address.id === group.address.id
      )
      if (index > -1) {
        const inCart = this.cartList[index]
        const setIds = new Set()
        group.palletIds.forEach(id => setIds.add(id))
        inCart.palletIds.forEach(id => setIds.add(id))
        inCart.palletIds = [...setIds]
        group.inCartCount = group.palletIds.length
      } else {
        if (
          this.totalSelected + group.palletIds.length <=
          MAX_PALLETS_IN_SHIPMENT
        ) {
          this.cartList.push({
            id: group.id,
            goodsIds: group.goodsIds,
            orderId: group.orderId,
            riiOrderNo: group.riiOrderNo,
            riiContainerNo: group.riiContainerNo,
            address: group.address,
            palletIds: group.palletIds
          })
          this.cartList.sort((a, b) => a.address.id - b.address.id)
          group.inCartCount = group.palletIds.length
        } else {
          this.$message.error('More than 90 pallets selected!')
        }
      }
    },
    addAddressStockGroup(group) {
      group.orders.forEach(order => {
        this.addStockGroup(order)
      })
      group.inCartCount = group.palletIds.length
    },
    removeFromCart(group) {
      const index = this.cartList.findIndex(
        one =>
          one.orderId === group.orderId && one.address.id === group.address.id
      )
      this.cartList.splice(index, 1)
      // updated in cart count
      const target = this.stockPage.records.find(one => one.id === group.id)
      if (target) target.inCartCount = 0
    },
    palletInCart(id) {
      return this.cartList.findIndex(one => one.id === id) > -1
    },
    createShipment() {
      this.goToShipmentPlace({ mode: ShipmentCreation })
    },
    updateShipment() {
      // if cart is empty, clear all pallets in current shipment
      if (this.cartList.length === 0) {
        this.$confirm(
          `Clean all pallets in shipment ${this.shipmentNo}`,
          'Warning',
          {
            confirmButtonText: 'OK',
            cancelButtonText: 'Cancel',
            type: 'warning'
          }
        )
          .then(() => {
            const palletIds = this.shipmentDetail.originalGroups.reduce(
              (init, cur) => init.concat(cur.palletIds),
              []
            )
            modifyShipmentPallets(this.shipmentDetail.id, [], palletIds).then(
              res => {
                this.goToShipmentDetail(this.shipmentDetail.no)
              }
            )
          })
          .catch(() => { })
      } else {
        localStorage.setItem(this.shipmentNo, JSON.stringify(this.cartList))
        this.goToShipmentPlace({
          mode: ShipmentUpdate,
          no: this.shipmentNo,
          id: this.shipmentDetail.id
        })
      }
    },
    syncGroupSelected() {
      if (this.selectedTab === AddressGroup) {
        this.syncAddressGroupSelected()
      } else {
        this.syncOrderGroupSelected()
      }
    },
    syncAddressGroupSelected() {
      this.stockPage?.records.forEach(one => {
        one.inCartCount = one.orders.map(order => {
          const inCart = this.cartList?.find(group => order.id === group.id)
          return inCart?.palletIds?.length ?? 0
        })
          .reduce((acc, num) => acc + num, 0)
      })
    },
    syncOrderGroupSelected() {
      this.stockPage?.records.forEach(one => {
        // check in cart
        const inCart = this.cartList?.find(group => one.id === group.id)
        one.inCartCount = inCart?.palletIds?.length ?? 0
      })
    },
    async getShipmentDetail() {
      if (this.shipmentNo === '') return
      this.loadingCart = true
      const page = await queryShipment({ nos: [this.shipmentNo] })
      this.shipmentDetail = page.records[0]
      // make current cartList to pallets in shipment
      const selectedStock = await searchStock({
        shipmentId: this.shipmentDetail.id,
        onlyShipped: true,
        size: -1
      })
      const addressIds = selectedStock.records
        .filter(one => !!one.addressId)
        .map(one => one.addressId)
      const addressPage = await queryAddress({ addressIds, size: -1 })
      const addressMap = Object.fromEntries(
        addressPage.records.map(one => [one.id, one])
      )
      const cartList = selectedStock.records.map(one => {
        return {
          id: one.orderId + '/' + one.groupName + '/' + one.addressId,
          goodsIds: one?.goodsIds?.split(',') ?? [],
          orderId: one.orderId,
          riiOrderNo: one.riiOrderNo,
          riiContainerNo: one.riiContainerNo,
          address: addressMap[one.addressId],
          groupName: one.groupName,
          palletIds: one?.palletIds?.split(',') ?? []
        }
      })
      this.shipmentDetail.originalGroups = [...cartList]
      this.$set(this, 'cartList', cartList)
      this.loadingCart = false
    },
    async doStockSearch() {
      // if (this.selectedTab === AddressGroup) return
      this.loadingStock = true
      const searchForm = StockSearchFormWrapUp(this.searchForm)
      const stocks = await searchStock({
        ...searchForm,
        searchByAddress: this.selectedTab === AddressGroup,
        hold: [SHIPPED, AddressGroup].includes(this.selectedTab) ? undefined : this.selectedTab,
        onlyShipped: this.selectedTab === SHIPPED
      })
      if (this.selectedTab !== AddressGroup) {
        const addressIds = stocks.records
          .filter(one => !!one.addressId)
          .map(one => one.addressId)
        const addressPage = await queryAddress({ addressIds, size: -1 })
        const addressMap = Object.fromEntries(
          addressPage.records.map(one => [one.id, one])
        )
        stocks.records.forEach(one => {
          if (one.type === LocalOrder) one.groupName = ''
          one.id = `${one.orderId}/${one.addressId}/${one.groupName}`
          if (one.shipmentId) {
            one.id += `/${one.shipmentId}`
          }
          one.goodsIds = one?.goodsIds?.split(',') ?? []
          one.palletIds = one?.palletIds?.split(',') ?? []
          one.address = addressMap[one.addressId]
          one.inCartCount = 0
        })
      } else {
        stocks.records = stocks.records.map(one => {
          const { stockList, ...address } = one
          const palletIds = []
          const goodsIds = []
          const orders = one.stockList.map(order => {
            const one = {
              ...order,
              id: `${order.orderId}/${address.id}/${order.groupName}`,
              addressId: address.id,
              address
            }
            one.goodsIds = one?.goodsIds?.split(',') ?? []
            one.palletIds = one?.palletIds?.split(',') ?? []
            palletIds.push(...one.palletIds)
            goodsIds.push(...one.goodsIds)
            return one
          })

          return {
            id: address.id,
            addressId: address.id,
            address,
            orders,
            goodsIds,
            palletIds,
            inCartCount: 0
          }
        })
      }
      this.stockPage = stocks
      this.loadingStock = false
      this.syncGroupSelected()
    },
    getGroupPalletsToShow(row) {
      if (this.selectedTab === HOLD) return row.holdPalletIds
      return row.leftPalletIds
    },
    openNewDialog() {
      this.$refs.dialog.open()
    },
    createSplitJob() {
      this.goToPalletSplitJob({
        mode: SplitJobCreate,
        tab: this.PackingListTab
      })
    },
    async exportPOs() {
      const goodsOrderMap = new Map()
      const goodsIds = this.cartList.flatMap(one => {
        one.goodsIds.forEach(id => {
          goodsOrderMap.set(id, one.riiOrderNo)
        })
        return one.goodsIds
      })
      const goodsPage = await queryGoods({
        ids: goodsIds,
        size: -1
      })
      const goods = goodsPage.records
      const riiOrderNos = [...new Set(this.cartList.map(one => one.riiOrderNo))]
      const ordersPage = await queryOrders({
        statusList: [OrderCreated, OrderProcessing],
        riiOrderNos,
        size: -1
      })
      const orders = ordersPage.records
      const orderMap = Object.fromEntries(orders.map(one => [one.riiOrderNo, one]))
      const rows = goods.map(good => {
        const riiOrderNo = goodsOrderMap.get(good.id)
        const order = orderMap[riiOrderNo]
        return {
          'RII Order No.': order.riiOrderNo,
          'Container No.': order.containerNo,
          'Reference No.': good.referenceNo,
          'Shipment No.': good.shipmentNo,
          Address: good.addressCode
        }
      })
      const worksheet = XLSX.utils.json_to_sheet(rows, {
        header: ['RII Order No.', 'Container No.', 'Reference No.', 'Shipment No.', 'Address']
      })
      worksheet['!cols'] = [{ wch: 25 }, { wch: 25 }, { wch: 25 }, { wch: 25 }, { wch: 25 }]
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'POs')
      const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = 'POs.xlsx'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  },
  mounted() {
    this.debouncedFetch()
    this.$set(
      this,
      'cartList',
      JSON.parse(localStorage.getItem('cartList')) ?? []
    )
    PagesMessage.on(
      PagesMessage.eventMap.REFRESH_STOCK_PAGE,
      this.debouncedFetch
    )
  }
}
</script>
<style lang="scss" scoped>
@import '@/assets/styles/common.scss';

.outer-container {
  height: 100%;
  overflow-x: hidden;
  overflow-y: hidden;
}

.mode-info {
  padding-left: 30px;
  height: 42px;
  align-items: center;

  .title {
    font-size: 24px;

    @extend .bold-oswald;
  }
}

.table-header {
  justify-content: flex-start;
  margin-left: 30px;

  @extend .f-r-c-c;

  gap: 10px;
}

.mode-actions {
  margin-left: auto;
  padding-right: 20px;
  gap: 10px;
  align-items: center;

  .el-button {
    margin: 0;
  }

}

.page-container {
  height: calc(100% - 57px);
  overflow: hidden;

  .left {
    width: 100%;
    overflow-y: scroll;
    transition: width 0.3s;
    transition-timing-function: ease;

    &.animate {
      width: calc(100% - 300px);
    }

    &::-webkit-scrollbar {
      display: none;
    }

    .stock-list {
      margin: 0 20px;
      margin-top: 1px;
      min-height: 100%;
      border-radius: $home-card-border-radius;
      padding: $home-card-padding-y $home-card-padding-x;
      background-color: white;

      .header {
        gap: 20px;

        .el-tabs {
          flex-grow: 1;
        }
      }

      .address-group {
        background-color: red;
        height: calc(100% - 92px);
        overflow: hidden;
      }
    }

    .group-expands {
      .actions {
        margin-bottom: 10px;

        .el-button {
          font-size: 14px;
        }
      }

      .tables {
        gap: 20px;
        margin-right: 60px;

        @extend .inner-table-override;
      }

      @extend .global-margin;
    }
  }

  .right {
    width: 0;
    border-radius: 4px;
    position: absolute;
    height: calc(100% - 117px);
    right: 20px;
    overflow-x: hidden;
    opacity: 0;
    transition: width 0.5s, opacity 0.3s;
    animation-direction: reverse;
    transition-timing-function: ease;
    display: flex;
    flex-direction: column;
    background: white;

    &.animate {
      width: 280px;
      opacity: 1;
    }

    .cart-header {
      min-width: 200px;
      font-size: 14px;
      font-weight: bold;
      padding: 10px 20px;
      padding-bottom: 5px;
      border-bottom: 1px solid #ccc;
      flex-shrink: 0;

      .selected-counts {
        font-family: 'oswald bold', sans-serif;
        font-size: 26px;
      }

      .el-button {
        font-weight: 400;
      }
    }

    .cart-list {
      margin-left: 0 !important;
      margin-top: 0;
      margin-bottom: 0;
      flex: 1;
      overflow: hidden;

      .pallet-selected {
        gap: 10px;
        padding: 0 15px;
        height: 100%;
        overflow-y: scroll;

        .group-address {
          font-family: 'oswald light', sans-serif;
          font-size: 24px;
          font-weight: 600;

          ::v-deep .code {
            width: 100px !important;
          }
        }

        .group-order-no {
          font-family: 'oswald light', sans-serif;
          font-size: 18px;
        }

        .pallet-counts {
          font-family: 'oswald bold', sans-serif;
          font-size: 32px;
        }

        .trash {
          font-size: 28px;
          margin-left: 20px;
        }

        .cart-group {
          padding: 10px 10px;
          border-bottom: 1px solid rgba($business-color, 0.6);

          &:hover {
            cursor: pointer;
          }

          .others {
            gap: 5px;
          }
        }
      }
    }
  }
}

.status {
  gap: 5px;

  @extend .f-r-c-c;

  .spot {
    width: 16px;
    height: 16px;
    border-radius: 4px;
  }

  span {
    font-size: 14px;
    font-family: 'oswald bold', sans-serif;
  }
}

.in-cart-count {
  font-size: 18px;
  font-weight: bold;
  color: black;
}

.new-shipment-or-job {
  gap: 1rem;
  height: 100px;

  .left,
  .right {
    flex-basis: 40%;
    flex-grow: 1;
    flex-shrink: 0;
    background-color: rgba($business-color, 0.85);
    border-radius: $home-card-border-radius;
    padding: 1rem;
    gap: 1rem;
    user-select: none;

    &:hover {
      cursor: pointer;
      background-color: rgba($business-color, 0.7);
    }

    &:active {
      background-color: rgba($business-color, 0.95);
    }

    .but-text {
      font-size: 24px;
      color: white;
    }
  }

  .left {
    background-color: rgba($deep-green, 0.85);

    &:hover {
      cursor: pointer;
      background-color: rgba($deep-green, 0.7);
    }

    &:active {
      background-color: rgba($deep-green, 0.95);
    }
  }
}
</style>
<style>
.row_min_height {
  height: 61px !important;
}
</style>
